/**
 * InvoiceMissingWORow — one row of the AlertsCenter for the
 * "invoice_missing_wo" alert type.
 *
 * On success (POST /api/commercial/invoices/:id/create-work-order):
 *   • removeLocal(entity_id) → alert disappears immediately
 *   • toast with the new WO number
 *   • background refetch to reconcile
 *   • DOES NOT open the WO modal (Sprint E · Decision 1a)
 *
 * On error: button re-enables, toast with the message, alert stays.
 *
 * GP 1.0 · Sprint E · Feb 2026
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

function TimeAgo({ createdAt }) {
  const { t } = useI18n();
  const label = useMemo(() => {
    if (!createdAt) return "";
    const ms = Date.now() - new Date(createdAt).getTime();
    if (Number.isNaN(ms) || ms < 0) return t("alert_time_just_now");
    const min = Math.floor(ms / 60_000);
    if (min < 1) return t("alert_time_just_now");
    if (min < 60) return t("alert_time_minutes", { n: min });
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return t("alert_time_hours", { n: hrs });
    const days = Math.floor(hrs / 24);
    return t("alert_time_days", { n: days });
  }, [createdAt, t]);
  return (
    <span
      data-testid="alert-row-time-ago"
      className="font-mono-tech text-[0.65rem] text-[var(--text-secondary)] tracking-wide"
    >
      {label}
    </span>
  );
}

export default function InvoiceMissingWORow({ alert, onResolved, onRefetch }) {
  const { t } = useI18n();
  const [processing, setProcessing] = useState(false);
  const ctx = alert.context || {};
  const label = alert.primary_action?.label_key
    ? t(alert.primary_action.label_key)
    : t("inv_detail_create_wo_btn");

  const createWorkOrder = async () => {
    if (processing || !alert.primary_action?.endpoint) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(
        `${API}${alert.primary_action.endpoint.replace(/^\/api/, "")}`,
        {},
        { withCredentials: true }
      );
      // Optimistic removal FIRST — decision 1a: alert disappears immediately.
      onResolved && onResolved(alert.entity_id);
      const woNumber = data?.work_order_number || "";
      if (data?.already_existed) {
        toast.success(t("inv_detail_wo_existed", { n: woNumber }));
      } else {
        toast.success(t("inv_detail_wo_created", { n: woNumber }));
      }
      // Background reconciliation — do not await.
      if (onRefetch) onRefetch();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || t("inv_detail_wo_create_err")
      );
      setProcessing(false);
    }
  };

  return (
    <div
      data-testid={`alert-row-${alert.entity_id}`}
      className="border-b border-[var(--border)] px-4 py-3 flex items-start gap-3 hover:bg-[var(--surface-muted,#f7f7f7)]"
    >
      <div className="mt-0.5 text-amber-600 font-mono-tech text-lg leading-none">
        ⚠
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            data-testid="alert-row-invoice-number"
            className="font-display font-bold text-sm tracking-tight"
          >
            {alert.entity_number || "—"}
          </span>
          <span
            data-testid="alert-row-company"
            className="text-xs text-[var(--text-secondary)] truncate"
          >
            {ctx.company || "—"}
          </span>
          <span className="ml-auto">
            <TimeAgo createdAt={ctx.created_at} />
          </span>
        </div>
        <div className="font-mono-tech text-[0.65rem] uppercase tracking-wider text-amber-700 mt-0.5">
          {t(alert.title_key || "alert_invoice_missing_wo_title")}
        </div>
        <div className="mt-2">
          <button
            data-testid={`alert-row-create-wo-${alert.entity_id}`}
            onClick={createWorkOrder}
            disabled={processing}
            className="font-mono-tech text-[0.7rem] font-bold bg-black text-white px-3 py-1.5 hover:bg-[var(--accent-magenta,#E5007D)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}
